import type { ReactNode } from 'react';

type SkeletonKind = 'kpi' | 'table' | 'report' | 'detail' | 'wizard';

/** Placeholders de carga — preferir a spinners de pantalla completa. */
export function AccountingSkeleton({
  kind = 'table',
  rows = 5,
  className = '',
}: {
  kind?: SkeletonKind;
  rows?: number;
  className?: string;
}) {
  if (kind === 'kpi') {
    return (
      <div className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-3 ${className}`} aria-busy="true" aria-label="Cargando indicadores">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-lg border bg-white p-4 animate-pulse">
            <div className="h-3 w-20 bg-slate-200 rounded" />
            <div className="mt-3 h-7 w-28 bg-slate-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (kind === 'wizard') {
    return (
      <div className={`space-y-4 ${className}`} aria-busy="true" aria-label="Cargando proceso">
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-9 w-28 bg-slate-200 rounded-md animate-pulse" />
          ))}
        </div>
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div className="rounded-lg border bg-white p-4 space-y-3 animate-pulse">
            <div className="h-3 w-32 bg-slate-200 rounded" />
            <div className="h-10 w-full bg-slate-100 rounded" />
            <div className="h-10 w-2/3 bg-slate-100 rounded" />
          </div>
          <div className="rounded-lg border bg-slate-50 p-4 space-y-2 animate-pulse">
            <div className="h-3 w-20 bg-slate-200 rounded" />
            <div className="h-4 w-full bg-slate-200 rounded" />
            <div className="h-4 w-3/4 bg-slate-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (kind === 'detail') {
    return (
      <div className={`space-y-3 animate-pulse ${className}`} aria-busy="true" aria-label="Cargando detalle">
        <div className="h-5 w-48 bg-slate-200 rounded" />
        <div className="h-4 w-full bg-slate-100 rounded" />
        <div className="h-4 w-5/6 bg-slate-100 rounded" />
        <div className="h-32 w-full bg-slate-100 rounded-lg" />
      </div>
    );
  }

  if (kind === 'report') {
    return (
      <div className={`space-y-4 ${className}`} aria-busy="true" aria-label="Cargando reporte">
        <AccountingSkeleton kind="kpi" />
        <div className="rounded-lg border bg-white p-4 h-56 animate-pulse bg-slate-50" />
        <AccountingSkeleton kind="table" rows={6} />
      </div>
    );
  }

  // table
  return (
    <div className={`rounded-lg border bg-white overflow-hidden ${className}`} aria-busy="true" aria-label="Cargando tabla">
      <div className="border-b bg-gray-50 px-3 py-2 flex gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-3 w-16 bg-slate-200 rounded animate-pulse" />
        ))}
      </div>
      <div className="divide-y">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="px-3 py-3 flex gap-4 animate-pulse">
            <div className="h-4 w-12 bg-slate-100 rounded" />
            <div className="h-4 w-24 bg-slate-100 rounded" />
            <div className="h-4 flex-1 bg-slate-100 rounded" />
            <div className="h-4 w-16 bg-slate-100 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function AccountingEmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div
      className="rounded-lg border border-dashed border-slate-200 bg-slate-50/80 px-6 py-10 text-center"
      role="status"
    >
      <p className="text-sm font-medium text-gray-900">{title}</p>
      {description && <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

export function AccountingErrorPanel({
  title = 'No se pudo completar',
  message,
  detail,
  onRetry,
}: {
  title?: string;
  message: string;
  detail?: string | null;
  onRetry?: () => void;
}) {
  return (
    <div
      className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
      role="alert"
    >
      <p className="font-semibold">{title}</p>
      <p className="mt-0.5">{message}</p>
      {detail && (
        <details className="mt-2 text-xs text-red-800/80">
          <summary className="cursor-pointer">Detalle técnico</summary>
          <pre className="mt-1 whitespace-pre-wrap break-all font-mono">{detail}</pre>
        </details>
      )}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 px-3 py-1.5 text-sm rounded-md border border-red-300 bg-white text-red-900 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400"
        >
          Reintentar
        </button>
      )}
    </div>
  );
}
